/**
 * 患者字段标准 - 前端权威来源 (Single Source of Truth)
 *
 * 与 backend/patient/domain/patient_standard.py 1:1 对应，
 * 由 backend/tests/unit/test_patient_standard_contract.py 自动比对。
 * 所有患者相关的字段元数据、校验规则、兼容映射都从这里派生。
 *
 * @module patient-management/shared/domain/patientStandard
 */

export interface PatientFieldSpec {
  /** 存储/传输字段名（snake_case，与后端一致） */
  key: string;
  /** 前端驼峰字段名（camelCase） */
  camelKey: string;
  /** 中文标签 */
  label: string;
  /** 创建时是否必填 */
  required: boolean;
  /** 是否为 PII（个人隐私信息） */
  pii: boolean;
  /** 是否为计算字段（不存储） */
  computed: boolean;
  /** 取值受控的值域键（/dictionaries/{system}） */
  codeSystem?: string;
}

// ==================== 标准字段列表 ====================

export const PATIENT_FIELD_SPECS = [
  // 标识
  {
    key: "patient_id",
    camelKey: "patientId",
    label: "患者业务ID",
    required: false,
    pii: false,
    computed: false,
  },
  // 基本信息
  {
    key: "name",
    camelKey: "name",
    label: "姓名",
    required: true,
    pii: false,
    computed: false,
  },
  {
    key: "gender",
    camelKey: "gender",
    label: "性别",
    required: true,
    pii: false,
    computed: false,
    codeSystem: "gender",
  },
  {
    key: "birth_date",
    camelKey: "birthDate",
    label: "出生日期",
    required: true,
    pii: false,
    computed: false,
  },
  {
    key: "birth_date_estimated",
    camelKey: "birthDateEstimated",
    label: "出生日期为估计值",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "age",
    camelKey: "age",
    label: "年龄",
    required: false,
    pii: false,
    computed: true,
  },
  // 证件（id_card 列名保留，语义为证件号码）
  {
    key: "id_type",
    camelKey: "idType",
    label: "证件类别",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "id_type",
  },
  {
    key: "id_card",
    camelKey: "idCard",
    label: "证件号码",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "no_id_reason",
    camelKey: "noIdReason",
    label: "无证件原因",
    required: false,
    pii: false,
    computed: false,
  },
  // 联系方式（PII）；本人电话与联系人电话至少一项（L0）
  {
    key: "phone",
    camelKey: "phone",
    label: "本人电话",
    required: false,
    pii: true,
    computed: false,
  },
  // 人口学（值域代码）
  {
    key: "nationality",
    camelKey: "nationality",
    label: "国籍",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "nationality",
  },
  {
    key: "ethnicity",
    camelKey: "ethnicity",
    label: "民族",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "ethnicity",
  },
  {
    key: "marital_status",
    camelKey: "maritalStatus",
    label: "婚姻状况",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "marital_status",
  },
  {
    key: "occupation",
    camelKey: "occupation",
    label: "职业",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "occupation",
  },
  {
    key: "education",
    camelKey: "education",
    label: "文化程度",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "education",
  },
  // 地址（区划代码明文，详细地址密文）
  {
    key: "birth_place_region",
    camelKey: "birthPlaceRegion",
    label: "出生地",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "region",
  },
  {
    key: "native_place_region",
    camelKey: "nativePlaceRegion",
    label: "籍贯",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "region",
  },
  {
    key: "current_address_region",
    camelKey: "currentAddressRegion",
    label: "现住址区划",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "region",
  },
  {
    key: "address",
    camelKey: "address",
    label: "现住址详细地址",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "current_postcode",
    camelKey: "currentPostcode",
    label: "现住址邮编",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "registered_address_region",
    camelKey: "registeredAddressRegion",
    label: "户口地址区划",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "region",
  },
  {
    key: "registered_address",
    camelKey: "registeredAddress",
    label: "户口详细地址",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "registered_postcode",
    camelKey: "registeredPostcode",
    label: "户口地址邮编",
    required: false,
    pii: false,
    computed: false,
  },
  // 工作单位
  {
    key: "workplace_name",
    camelKey: "workplaceName",
    label: "工作单位",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "workplace_address",
    camelKey: "workplaceAddress",
    label: "单位地址",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "workplace_phone",
    camelKey: "workplacePhone",
    label: "单位电话",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "workplace_postcode",
    camelKey: "workplacePostcode",
    label: "单位邮编",
    required: false,
    pii: false,
    computed: false,
  },
  // [LEGACY] 自由文本，表单不再写入
  {
    key: "country",
    camelKey: "country",
    label: "国家（旧）",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "province",
    camelKey: "province",
    label: "省份（旧）",
    required: false,
    pii: false,
    computed: false,
  },
  // 联系人
  {
    key: "emergency_contact",
    camelKey: "emergencyContact",
    label: "联系人姓名",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "emergency_relation",
    camelKey: "emergencyRelation",
    label: "联系人关系",
    required: false,
    pii: false,
    computed: false,
    codeSystem: "contact_relation",
  },
  {
    key: "emergency_phone",
    camelKey: "emergencyPhone",
    label: "联系人电话",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "emergency_address",
    camelKey: "emergencyAddress",
    label: "联系人地址",
    required: false,
    pii: true,
    computed: false,
  },
  {
    key: "notes",
    camelKey: "notes",
    label: "备注",
    required: false,
    pii: false,
    computed: false,
  },
  // 分类与标签
  {
    key: "patient_type",
    camelKey: "patientType",
    label: "患者类型",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "organization",
    camelKey: "organization",
    label: "所属组织",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "vip_level",
    camelKey: "vipLevel",
    label: "VIP等级",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "tags",
    camelKey: "tags",
    label: "标签列表",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "tag_names",
    camelKey: "tagNames",
    label: "标签名称",
    required: false,
    pii: false,
    computed: false,
  },
  // 微信绑定
  {
    key: "wechat_openid",
    camelKey: "wechatOpenid",
    label: "微信OpenID",
    required: false,
    pii: false,
    computed: false,
  },
  // 状态
  {
    key: "status",
    camelKey: "status",
    label: "状态",
    required: false,
    pii: false,
    computed: false,
  },
  // 时间戳
  {
    key: "created_at",
    camelKey: "createdAt",
    label: "创建时间",
    required: false,
    pii: false,
    computed: false,
  },
  {
    key: "updated_at",
    camelKey: "updatedAt",
    label: "更新时间",
    required: false,
    pii: false,
    computed: false,
  },
] as const satisfies readonly PatientFieldSpec[];

// ==================== 运行时查找 ====================

export const PATIENT_FIELD_MAP: Record<string, PatientFieldSpec> =
  Object.fromEntries(PATIENT_FIELD_SPECS.map((s) => [s.key, s]));

export const PATIENT_CAMEL_MAP: Record<string, PatientFieldSpec> =
  Object.fromEntries(PATIENT_FIELD_SPECS.map((s) => [s.camelKey, s]));

export const PII_FIELDS: ReadonlySet<string> = new Set(
  PATIENT_FIELD_SPECS.filter((s) => s.pii).map((s) => s.key),
);

export const REQUIRED_FIELDS: ReadonlySet<string> = new Set(
  PATIENT_FIELD_SPECS.filter((s) => s.required).map((s) => s.key),
);

// ==================== 字段名兼容映射 ====================
// 少量无法从 key/camelKey 推导的历史字段名 → 标准字段名
const EXPLICIT_LEGACY_FIELD_ALIASES: Readonly<Record<string, string>> = {
  phone_number: "phone",
  id_card_number: "id_card",
  patient_seq_id: "patient_id",
  patient_name: "name",
  patient_gender: "gender",
  patient_age: "age",
};

/**
 * 任意兼容字段名 -> 标准 snake_case 字段名。
 * 标准 key 与 camelKey 从 PATIENT_FIELD_SPECS 自动生成。
 */
export const PATIENT_FIELD_ALIAS_MAP: Readonly<Record<string, string>> =
  Object.freeze({
    ...Object.fromEntries(
      PATIENT_FIELD_SPECS.flatMap((spec) => [
        [spec.key, spec.key],
        [spec.camelKey, spec.key],
      ]),
    ),
    ...EXPLICIT_LEGACY_FIELD_ALIASES,
  });

/**
 * 将任意字段名解析为标准 key
 */
export function resolveFieldKey(fieldName: string): string {
  return PATIENT_FIELD_ALIAS_MAP[fieldName] ?? fieldName;
}

/** 获取标准患者字段的全部读取候选名。 */
export function getPatientFieldCandidates(
  fieldName: string,
): readonly string[] {
  const standardKey = resolveFieldKey(fieldName);
  const spec = PATIENT_FIELD_MAP[standardKey];
  const explicitAliases = Object.entries(EXPLICIT_LEGACY_FIELD_ALIASES)
    .filter(([, target]) => target === standardKey)
    .map(([alias]) => alias);
  return spec
    ? [...new Set([spec.key, spec.camelKey, ...explicitAliases])]
    : [fieldName];
}

/** GB/T 2261.1 性别名称（与 backend/patient/domain/demographics.py 一致） */
export type PatientGender = "男" | "女" | "未知" | "未说明";

export const PATIENT_GENDER_OPTIONS: readonly PatientGender[] = [
  "男",
  "女",
  "未知",
  "未说明",
];

/** 存储/计算边界：把多种性别编码归一为领域值（历史「其他」归为「未说明」）。 */
export function normalizePatientGender(value: unknown): PatientGender {
  if (typeof value !== "string" && typeof value !== "number") return "未知";
  const normalized = String(value).trim().toLowerCase();
  if (["男", "男性", "male", "m", "1"].includes(normalized)) return "男";
  if (["女", "女性", "female", "f", "2"].includes(normalized)) return "女";
  if (["未说明", "未说明的性别", "其他", "other", "9"].includes(normalized))
    return "未说明";
  return "未知";
}

/** 存储/计算边界：年龄归一为非负整数；展示后缀不在此处添加。 */
export function normalizePatientAge(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const raw =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value).match(/\d+/)?.[0] ?? "", 10);
  if (!Number.isFinite(raw) || raw < 0) return undefined;
  return Math.floor(raw);
}
